import clsx from 'clsx';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * Shared phase-aware skeleton primitives for per-tool renderers (Phase 2
 * Session 3). Each per-renderer file imports these and composes the
 * tool-specific shape from them.
 *
 * Visual rule: skeletons get a static panel-bg shell; in-progress phases
 * get a subtle `animate-pulse-status` accent border to signal "working".
 */

interface PhaseShellProps {
  variant: 'skeleton' | 'in-progress' | 'aborted';
  title: string;
  children?: React.ReactNode;
}

/**
 * Common outer shell for input-streaming / executing / aborted phases.
 * Renders a 1-line title + optional children. The accent border on the
 * `in-progress` variant matches the directive §7.1 MetricsCard skeleton spec
 * ("subtle pulsing accent border") and is reused across all 7 renderers for
 * consistency.
 */
export function PhaseShell({ variant, title, children }: PhaseShellProps) {
  return (
    <div
      className={clsx(
        'space-y-2',
        variant === 'in-progress' && 'border border-accent/15 rounded-[2px] px-2 py-1.5 animate-pulse-status',
        variant === 'aborted' && 'opacity-60',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono font-semibold tracking-[1.2px] uppercase text-text-muted">
          {title}
        </span>
        {variant === 'aborted' && (
          <span className="text-[10px] font-mono font-semibold px-1.5 py-px rounded-[2px] border text-warn bg-warn-dim border-warn/15">
            ABORTED
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

/** A single skeleton metric slot — used by `<MetricsCard>` skeleton. */
export function MetricSlotSkeleton({ label }: { label: string }) {
  return (
    <div className="bg-bg border border-panel-border rounded-[2px] px-2 py-1.5">
      <div className="text-micro font-mono text-text-dim uppercase tracking-[0.8px]">{label}</div>
      <Skeleton className="h-4 w-12 mt-1" />
    </div>
  );
}

/** Empty matrix shell sized to dimensions; used by `<Sensitivity2DMatrix>` skeleton. */
export function MatrixShell({
  rows,
  cols,
  rowLabel,
  colLabel,
}: {
  rows: number;
  cols: number;
  rowLabel?: string;
  colLabel?: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse">
        <thead>
          <tr>
            <th className="text-xs font-mono font-semibold text-text-dim px-2 py-1 border-b border-panel-border">
              {rowLabel ?? '...'} \ {colLabel ?? '...'}
            </th>
            {Array.from({ length: cols }).map((_, i) => (
              <th
                key={i}
                className="text-center px-2 py-1 border-b border-panel-border"
              >
                <Skeleton className="h-3 w-8 mx-auto" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, ri) => (
            <tr key={ri}>
              <td className="px-2 py-1 border-r border-panel-border">
                <Skeleton className="h-3 w-8" />
              </td>
              {Array.from({ length: cols }).map((_, ci) => (
                <td
                  key={ci}
                  data-cell-painted="false"
                  className="px-2 py-1.5 border border-panel-border/30 text-center"
                >
                  <Skeleton className="h-3 w-10 mx-auto" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** N placeholder rows — used by `<SensitivityTable>` skeleton. */
export function PlaceholderRows({ count, columns }: { count: number; columns: number }) {
  return (
    <table className="w-full border-collapse">
      <tbody>
        {Array.from({ length: count }).map((_, ri) => (
          <tr key={ri} className="border-b border-panel-border/50">
            <td className="px-2 py-1">
              <Skeleton className="h-3 w-12" />
            </td>
            {Array.from({ length: columns }).map((_, ci) => (
              <td key={ci} className="px-2 py-1 text-right">
                <Skeleton className="h-3 w-10 ml-auto" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Three skeleton info rows — used by `<ModelInfoPanel>` and `<DependencyTree>`. */
export function InfoRowsSkeleton({ rows = 3 }: { rows?: number } = {}) {
  return (
    <div className="space-y-1">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-3 w-full max-w-[60%]" />
      ))}
    </div>
  );
}
